import { ReuseModal } from "../../ReuseModal";
import { Link } from "react-router-dom";
import { ReactNode, useState } from "react";
import { Account, CRMData, IntegrationInfo } from "../../../../types/apiTypes";
import {
  campaignsApi,
  useCreateImportFromCRMMutation,
  useGetCRMUrlMutation,
  accountsApi,
  useEnterCompanyManuallyMutation,
  useUploadCampaignFilesMutation,
  useCheckForDataSourceMutation,
} from "../../../../api";
import { toast } from "react-toastify";
import { CustomToast } from "../../CustomToast";
import { getErrorMessage } from "../../../../utils/getErrorMessage";
import { UploadFromDesktopMethod } from "../../../products/CreateCampaignFlow/steps";
import { AppRoutes } from "../../../../types/enums";
import { Stack, Typography } from "@mui/material";
import { CustomButton } from "../../CustomButton";
import { PlusIcon, UpdateIcon, UploadDataIcon } from "../../../images";
import { useAppDispatch } from "../../../../hooks";
import { MismatchDataDesktopWrapper } from "../ResyncDataModal/MismatchDataDesktopWrapper";
import { MismatchDataCRMWrapper } from "../ResyncDataModal/MismatchDataCRMWrapper";
import { CRMUpdateMethod } from "../ResyncDataModal/CRMUpdateMethod";
import { EnterManually } from "../../../products/CreateCampaignFlow/steps/TargetAccountsStep/EnterManually";
import { NAME_REGEX, URL_REGEX } from "../../../../utils/regex";
import { MissingDataSourceModal } from "../MissingDataSourceModal";

const Header = ({
  title,
  text,
  icon,
  customText,
  isFirstUpload,
}: {
  title: string;
  text?: string;
  customText?: ReactNode;
  icon: ReactNode;
  isFirstUpload: boolean;
}) => {
  if (!isFirstUpload) return null;

  return (
    <Stack
      sx={{
        gap: "8px",
        padding: "16px",
        alignItems: "flex-start",
        backgroundColor: "custom.surfaceMuted",
        borderRadius: "6px",
      }}
    >
      <Stack flexDirection="row" gap="4px">
        {icon}
        <Typography
          component="span"
          variant="subtitle1"
          color="custom.textDarkBlue"
          fontWeight={500}
        >
          {title}
        </Typography>
      </Stack>

      {text && (
        <Typography component="span" variant="body1" color="text.secondary">
          {text}
        </Typography>
      )}
      {customText}
    </Stack>
  );
};

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  campaign_id: string;
  crmIntegration?: IntegrationInfo;
  hasFileImports: boolean;
};

export const UploadDataModal = ({
  isOpen,
  setIsOpen,
  campaign_id,
  crmIntegration,
  hasFileImports,
}: Props) => {
  const dispatch = useAppDispatch();
  const [companies, setCompanies] = useState<Account[]>([]);
  const [currentMethod, setCurrentMethod] = useState<string>("CRM");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [files, setFiles] = useState<File[]>([]);
  const [dataSourceList, setDataSourceList] = useState<CRMData | null>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isDataSourceModal, setIsDataSourceModal] = useState<boolean>(false);

  const [enterCompanyManually] = useEnterCompanyManuallyMutation();
  const [uploadFile] = useUploadCampaignFilesMutation();
  const [importFromCRM] = useCreateImportFromCRMMutation();
  const [getCRMUrl] = useGetCRMUrlMutation();
  const [checkForDataSource] = useCheckForDataSourceMutation();

  const isFromDesktop = currentMethod === "Desktop" && files.length > 0;
  const isFromCRM = currentMethod === "CRM" && !!dataSourceList;
  const isFromManually = currentMethod === "Manually" && companies.length > 0;

  const isNeedConnect =
    crmIntegration && !crmIntegration?.is_connected && currentMethod === "CRM";

  const navigationMethods = [
    { label: "CRM", isDisable: isFromDesktop },
    { label: "Desktop", isDisable: isFromDesktop },
    { label: "Manually", isDisable: isFromDesktop },
  ];

  const methods: Record<string, ReactNode> = {
    Desktop: (
      <MismatchDataDesktopWrapper
        isFirstUpload={true}
        setIsVisible={setIsVisible}
        isDisabled={isLoading}
        campaign_id={campaign_id}
      >
        <UploadFromDesktopMethod isLoading={isLoading} setFiles={setFiles} />
      </MismatchDataDesktopWrapper>
    ),
    CRM: (
      <MismatchDataCRMWrapper
        mismatches={{ total: 0, is_syncing: false }}
        isFirstUpload={true}
      >
        <CRMUpdateMethod
          actionType="upload"
          synced={dataSourceList?.crm}
          setIsVisible={setIsVisible}
          setDataSource={setDataSourceList}
          campaign_id={campaign_id}
          crmIntegration={crmIntegration}
        />
      </MismatchDataCRMWrapper>
    ),
    Manually: (
      <MismatchDataCRMWrapper
        mismatches={{ total: 0, is_syncing: false }}
        isFirstUpload={!!crmIntegration}
      >
        <EnterManually setCompanies={setCompanies} isDisabled={isLoading} />
      </MismatchDataCRMWrapper>
    ),
  };

  const headers: Record<string, ReactNode> = {
    Desktop: (
      <Header
        isFirstUpload={!hasFileImports}
        title="Upload Your Data for Buying Committee Insights"
        text={
          "Drag and drop your target accounts and contacts data to receive valuable buying committee insights" +
          " and engagement tracking. Gain access to external contacts search & update."
        }
        customText={
          <Typography component="span" variant="body1" color="text.secondary">
            Check{" "}
            <Link
              to={AppRoutes.CAMPAIGNS_REQUIREMENTS}
              style={{ textDecoration: "none" }}
              target="_blank"
            >
              <span style={{ color: "#0069C4" }}>Field requirements</span>
            </Link>{" "}
            to see the full list of accounts and contacts related fields.
          </Typography>
        }
        icon={<UploadDataIcon />}
      />
    ),
    CRM: (
      <Header
        isFirstUpload={!crmIntegration}
        title="Connect Your CRM"
        text="Utilize import & export operations, active updates tracking."
        icon={<UpdateIcon color="#1C3C6C" />}
      />
    ),
    Manually: (
      <Header
        isFirstUpload={true}
        title="Add Accounts Manually"
        text="Add target accounts names to search for buying committee and get roles segmentation insights."
        icon={<PlusIcon color="#1C3C6C" />}
      />
    ),
  };

  const submitFromDesktopHandler = async (
    campaign_id: string,
    dataSource: string,
  ) => {
    await uploadFile({
      campaign_id,
      files,
      import_contacts: true,
      data_source: dataSource
        ? dataSource === "CRM"
          ? "crm_data"
          : "infuse_data"
        : undefined,
    })
      .unwrap()
      .then(() => {
        toast.success(
          <CustomToast
            type={"success"}
            text={"Company data uploading was successful."}
            title={"Upload Success"}
          />,
        );
      })
      .catch((err) => {
        toast.error(
          <CustomToast
            type={"error"}
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
            title={"Upload Failed"}
          />,
        );
      });
  };

  const submitManuallyHandler = async (campaign_id: string) => {
    await enterCompanyManually({
      campaign_id,
      companies,
    })
      .unwrap()
      .then(() => {
        toast.success(
          <CustomToast
            type={"success"}
            text={"Company data uploading was successful."}
            title={"Upload Success"}
          />,
        );
      })
      .catch((err) => {
        toast.error(
          <CustomToast
            type={"error"}
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong during processing. Please try again"
            }
            title={"Changes Were Not Saved"}
          />,
        );
      });
  };

  const submitCRMHandler = async (
    campaign_id: string,
    import_contacts: boolean,
  ) => {
    let list_id: string | undefined;
    if (dataSourceList?.list_id && dataSourceList?.list_id.length > 0) {
      list_id = dataSourceList.list_id;
    }

    await importFromCRM({
      campaign_id,
      import_contacts,
      crm: dataSourceList?.crm!,
      list_id,
    })
      .unwrap()
      .then(() => {
        toast.success(
          <CustomToast
            type={"success"}
            text={"Company data uploading was successful."}
            title={"Upload success"}
          />,
        );
      })
      .catch((err) => {
        toast.error(
          <CustomToast
            type={"error"}
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong during processing. Please try again"
            }
            title={"Import data failed"}
          />,
        );
        setDataSourceList(null);
      });
  };

  const connectToCrmHandler = () => {
    getCRMUrl({
      campaign_id,
      action_type: "connect",
      redirect_path: location.pathname,
      crm: dataSourceList?.crm || crmIntegration?.integration || "",
    })
      .unwrap()
      .then((data) => {
        window.location.href = data.url;
      })
      .catch((err) =>
        toast.error(
          <CustomToast
            type="error"
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
            title="Connection failed."
          />,
        ),
      );
  };

  const submitHandler = async (dataSource: string = "") => {
    if (isNeedConnect) {
      connectToCrmHandler();
      setIsLoading(false);
      return;
    }

    if (isFromDesktop && !dataSource) {
      const withDataSource = await checkForDataSource(files)
        .unwrap()
        .then(({ has_data_source_column }) => has_data_source_column)
        .catch((err) =>
          toast.error(
            <CustomToast
              type={"error"}
              text={
                getErrorMessage(err).globalError ||
                "Something went wrong. Please try again."
              }
              title={"Something Went Wrong"}
            />,
          ),
        );

      setIsDataSourceModal(!withDataSource);
      if (!withDataSource) return;
    }

    setIsLoading(true);
    if (isFromManually) await submitManuallyHandler(campaign_id);
    if (isFromDesktop) await submitFromDesktopHandler(campaign_id, dataSource);
    if (isFromCRM) await submitCRMHandler(campaign_id, true);

    dispatch(
      accountsApi.util.invalidateTags([
        "analytics",
        "export-view",
        "list-view",
        "filters",
      ]),
    );

    dispatch(
      campaignsApi.util.invalidateTags([
        "summary",
        "engagement-config",
        "Campaign",
        "members",
        "company",
      ]),
    );

    setIsLoading(false);
    setIsOpen(false);
  };

  const submitMissingDataHandler = (dataSource: string) => {
    setIsDataSourceModal(false);
    void submitHandler(dataSource);
  };

  const submitCRMisDisabled = currentMethod === "CRM" && !dataSourceList;
  const submitDesktopIsDisabled = currentMethod === "Desktop" && !isFromDesktop;
  const submitManuallyIsDisabled =
    (currentMethod === "Manually" && companies.length === 0) ||
    !companies.every(
      ({ name, website_url }) =>
        name.trim() &&
        NAME_REGEX.test(name) &&
        (!website_url || URL_REGEX.test(website_url)),
    );

  return (
    <ReuseModal
      isHide={isVisible || isDataSourceModal}
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      title="Upload Data"
      maxWidth="610px"
      submitBtnText="Upload"
      submitBtnColor="#1C3C6C"
      onSubmit={() => submitHandler()}
      isLoading={isLoading}
      isDisabled={
        submitCRMisDisabled ||
        submitDesktopIsDisabled ||
        isLoading ||
        submitManuallyIsDisabled
      }
      isDisabledCancelBtn={isLoading}
    >
      <Stack gap="16px">
        <Stack flexDirection="row" gap="8px" flexWrap="nowrap">
          {navigationMethods.map(({ label, isDisable }) => (
            <CustomButton
              key={label}
              onClick={() => setCurrentMethod(label)}
              variant="text"
              type="button"
              sx={{
                gap: 0,
                textWrap: "nowrap",
                color: `${label === currentMethod ? "#1C3C6C" : "#4C545B"}`,
                cursor: `${label === currentMethod ? "auto" : "pointer"}`,
                backgroundColor: `${
                  label === currentMethod ? "#F1F1F1" : "#F1F1F14D"
                }`,
                "&:hover": {
                  color: "#1C3C6C",
                  backgroundColor: "#F1F1F1",
                },
              }}
              fullWidth
              disabled={isDisable || isLoading}
            >
              {label}
            </CustomButton>
          ))}
        </Stack>
        {headers[currentMethod]}
        {methods[currentMethod]}
      </Stack>

      {isDataSourceModal && (
        <MissingDataSourceModal
          isOpen={isDataSourceModal}
          onClose={() => setIsDataSourceModal(false)}
          onSubmit={submitMissingDataHandler}
        />
      )}
    </ReuseModal>
  );
};
